/**
 * Service / remote-access session dead — web JWT may still be valid.
 * Separate from full web session-dead (sessionDead.ts) so users stay signed in
 * while background service auth (tunnel, peers, remote library) is revoked.
 */

import { diagnosticLog } from '../utils/diagnosticLogger';

export type RemoteAccessRevokedPayload = {
  reason?: string;
  trigger?: string;
  remote_access_affected?: boolean;
  user_message?: string;
};

let _serviceSessionDead = false;
let _lastPayload: RemoteAccessRevokedPayload | null = null;
let _sideEffectsRegistered = false;
const listeners = new Set<() => void>();

function notifyListeners(): void {
  for (const fn of listeners) {
    try { fn(); } catch { /* ignore */ }
  }
}

/** Feature-detect connector payloads (fleet session_expired + v11.0.191 remote_access_revoked). */
export function isRemoteAccessRevocationPayload(
  payload: Record<string, unknown> | null | undefined,
): boolean {
  if (!payload || typeof payload !== 'object') return false;
  if (payload.remote_access_affected === true) return true;
  if (typeof payload.user_message === 'string' && payload.user_message.trim().length > 0) {
    return true;
  }
  const trigger = String(payload.trigger ?? '').toLowerCase();
  if (trigger.includes('auth_family') || trigger.includes('remote_access')) return true;
  const reason = String(payload.reason ?? '').toLowerCase();
  if (
    reason.includes('auth_family')
    || reason.includes('auth_json')
    || reason.includes('remote_access')
  ) {
    return true;
  }
  return false;
}

export function isServiceSessionDead(): boolean {
  return _serviceSessionDead;
}

export function getServiceSessionDeadPayload(): RemoteAccessRevokedPayload | null {
  return _lastPayload;
}

export function subscribeServiceSessionDead(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function shouldShowServiceSessionBanner(isAuthenticated: boolean): boolean {
  return isAuthenticated && _serviceSessionDead;
}

/** Connector session_expired / remote_access_revoked — does not sign the user out of the web session. */
export function markServiceSessionDead(
  source?: string,
  payload?: RemoteAccessRevokedPayload | null,
): void {
  const alreadyDead = _serviceSessionDead;
  _serviceSessionDead = true;
  _lastPayload = payload ?? null;
  if (!alreadyDead) {
    try {
      window.dispatchEvent(new CustomEvent('maestro:service-session-dead', {
        detail: { source: source ?? 'unknown', payload: _lastPayload },
      }));
    } catch { /* ignore */ }
  }
  notifyListeners();
}

export function markServiceSessionHealedViaReauth(): void {
  if (!_serviceSessionDead) return;
  try {
    diagnosticLog('info', 'other', 'service_session_healed_via_reauth', {
      reason: _lastPayload?.reason ?? null,
      trigger: _lastPayload?.trigger ?? null,
    });
  } catch { /* noop */ }
  resetServiceSessionDeadState();
}

export function markServiceSessionHealedFromIpc(): void {
  if (!_serviceSessionDead) return;
  try {
    diagnosticLog('info', 'other', 'service_session_healed_via_reauth', {
      heal_path: 'ipc_healthy',
      reason: _lastPayload?.reason ?? null,
    });
  } catch { /* noop */ }
  resetServiceSessionDeadState();
}

export function resetServiceSessionDeadState(): void {
  if (!_serviceSessionDead) return;
  _serviceSessionDead = false;
  _lastPayload = null;
  notifyListeners();
  try {
    window.dispatchEvent(new CustomEvent('maestro:service-session-healed'));
  } catch { /* ignore */ }
}

/** Stop BATON reconnect and radio refill when service auth is revoked. */
export function registerServiceSessionDeadSideEffects(): void {
  if (_sideEffectsRegistered || typeof window === 'undefined') return;
  _sideEffectsRegistered = true;
  window.addEventListener('maestro:service-session-dead', () => {
    void import('../radio/radioRefill').then((m) => m.clearRadioSession()).catch(() => {});
    void import('../handlers/ClientTransportHandler')
      .then((m) => m.getClientTransport().haltReconnectLoops())
      .catch(() => {});
  });
}
